import React, { useState } from 'react';

const V1 = '#7F00FF', V2 = '#B163FF', TEAL = '#3B82F6', GREEN = '#2ECC71', RED = '#F04155';
const MODES = [
  { key: 'climb', label: 'Climbing Stairs' },
  { key: 'robber', label: 'House Robber' },
  { key: 'coinchange', label: 'Coin Change' },
  { key: 'lis', label: 'Longest Incr. Subseq.' },
  { key: 'quiz', label: 'Quick Quiz' },
];

const CodePanel = ({ title, lines, active }) => (
  <div style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
    <div style={{ padding: '9px 14px', background: 'var(--bg3)', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>
      {title || 'Java'}
    </div>
    <pre style={{ margin: 0, padding: '12px 0', background: '#0E0B18', fontFamily: "'Fira Code','Consolas',monospace", fontSize: 12.5, lineHeight: 1.9, overflowX: 'auto' }}>
      {lines.map((line, i) => (
        <div key={i} style={{
          padding: '0 16px', color: i === active ? '#fff' : '#B7ACD6',
          background: i === active ? `${V1}45` : 'transparent',
          borderLeft: `3px solid ${i === active ? V2 : 'transparent'}`,
          whiteSpace: 'pre',
        }}>{line || ' '}</div>
      ))}
    </pre>
  </div>
);

const BoxRow = ({ label, values, getBoxState }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{ fontSize: 10.5, fontWeight: 800, color: 'var(--muted)', marginBottom: 7, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', minHeight: 46 }}>
      {values.length === 0 && <span style={{ fontSize: 12.5, color: 'var(--muted)', padding: '12px 0' }}>empty</span>}
      {values.map((v, i) => {
        const st = getBoxState ? getBoxState(i) : {};
        return (
          <div key={i} style={{
            width: 46, height: 46, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14, fontWeight: 800, fontFamily: "'Inter',sans-serif",
            background: st.bg || 'var(--bg3)', color: st.color || 'var(--text)',
            border: `1.5px solid ${st.border || 'var(--border)'}`, transition: 'all 0.2s ease',
          }}>{v === Infinity ? '∞' : v}</div>
        );
      })}
    </div>
  </div>
);

const smallInput = {
  width: 90, padding: '8px 10px', borderRadius: 9, border: '1px solid var(--border)',
  background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit',
};
const wideInput = { ...smallInput, width: 200 };
const smallBtn = (bg = V1, disabled = false) => ({
  padding: '8px 14px', borderRadius: 9, border: 'none', cursor: disabled ? 'default' : 'pointer',
  background: disabled ? 'var(--bg3)' : bg, color: disabled ? 'var(--muted)' : '#fff',
  fontSize: 12.5, fontWeight: 700, fontFamily: "'Satoshi','DM Sans',sans-serif", opacity: disabled ? 0.6 : 1,
});
const StepNav = ({ step, total, onPrev, onNext }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
    <button onClick={onPrev} disabled={step === 0} style={{
      padding: '8px 14px', borderRadius: 9, cursor: step === 0 ? 'default' : 'pointer',
      border: `1.5px solid ${step === 0 ? 'var(--border)' : V1}`, background: 'transparent',
      color: step === 0 ? 'var(--muted)' : V1, fontSize: 12.5, fontWeight: 700,
      fontFamily: "'Satoshi','DM Sans',sans-serif", opacity: step === 0 ? 0.6 : 1,
    }}>← Prev</button>
    <button onClick={onNext} disabled={step === total - 1} style={smallBtn(V1, step === total - 1)}>Next →</button>
    <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>Step {step + 1} of {total}</span>
  </div>
);

/* ─── Climbing Stairs ─────────────────────────────────────────────── */
const ClimbStairsMode = () => {
  const [n, setN] = useState(5);
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const run = () => {
    const size = Math.min(12, Math.max(1, n));
    const dp = new Array(size + 1).fill(0);
    const built = [];
    dp[1] = 1;
    built.push({ dp: [...dp], i: 1, note: 'dp[1] = 1 — only one way to reach step 1: a single 1-step move.' });
    if (size >= 2) {
      dp[2] = 2;
      built.push({ dp: [...dp], i: 2, note: 'dp[2] = 2 — either two 1-steps, or one 2-step.' });
    }
    for (let i = 3; i <= size; i++) {
      dp[i] = dp[i - 1] + dp[i - 2];
      built.push({ dp: [...dp], i, from: [i - 1, i - 2], note: `dp[${i}] = dp[${i - 1}] + dp[${i - 2}] = ${dp[i - 1]} + ${dp[i - 2]} = ${dp[i]} — reach step ${i} either from step ${i - 1} (one more 1-step) or step ${i - 2} (one more 2-step).` });
    }
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;
  const size = Math.min(12, Math.max(1, n));
  const getBoxState = i => {
    if (!cur) return {};
    if (i === cur.i) return { bg: `linear-gradient(135deg,${V1},${V2})`, border: V1, color: '#fff' };
    if (cur.from && cur.from.includes(i)) return { bg: `${TEAL}22`, border: TEAL, color: 'var(--text)' };
    return {};
  };

  const code = {
    lines: [
      'int climbStairs(int n) {',
      '    if (n <= 2) return n;',
      '    int[] dp = new int[n + 1];',
      '    dp[1] = 1; dp[2] = 2;',
      '    for (int i = 3; i <= n; i++) {',
      '        dp[i] = dp[i - 1] + dp[i - 2];',
      '    }',
      '    return dp[n];',
      '}',
    ],
    active: !cur ? -1 : cur.i <= 2 ? 3 : 5,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <BoxRow label={`dp[0..${size}] — ways to reach each step`} values={cur ? cur.dp : new Array(size + 1).fill(0)} getBoxState={getBoxState} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)' }}>n</span>
          <input value={n} onChange={e => setN(Math.min(12, Math.max(1, parseInt(e.target.value, 10) || 1)))} style={{ ...smallInput, width: 60 }} type="number" min={1} max={12} />
          <button onClick={run} style={smallBtn(V1)}>Build Table</button>
        </div>
        {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
        <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: '14px 0 0' }}>
          {!cur ? `Choose n (1–12), then Build Table — watch dp[i] get filled bottom-up from dp[i-1] and dp[i-2].` : cur.note}
        </p>
      </div>
      <CodePanel title="Java — Climbing Stairs (Bottom-Up DP)" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── House Robber ────────────────────────────────────────────────── */
const RobberMode = () => {
  const [input, setInput] = useState('2, 7, 9, 3, 1');
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const run = () => {
    const nums = input.split(',').map(s => Number(s.trim())).filter(v => !isNaN(v));
    if (nums.length === 0 || nums.length > 10) return;
    const dp = new Array(nums.length).fill(0);
    const built = [];
    dp[0] = nums[0];
    built.push({ dp: [...dp], i: 0, note: `dp[0] = nums[0] = ${nums[0]} — only one house, rob it.` });
    if (nums.length > 1) {
      dp[1] = Math.max(nums[0], nums[1]);
      built.push({ dp: [...dp], i: 1, note: `dp[1] = max(nums[0], nums[1]) = max(${nums[0]}, ${nums[1]}) = ${dp[1]} — rob whichever of the first two houses is worth more.` });
    }
    for (let i = 2; i < nums.length; i++) {
      const skip = dp[i - 1];
      const take = dp[i - 2] + nums[i];
      dp[i] = Math.max(skip, take);
      const action = take > skip ? 'robbing it wins' : 'skipping it wins';
      built.push({ dp: [...dp], i, from: [i - 1, i - 2], note: `House ${i} (value ${nums[i]}): skip → dp[${i - 1}] = ${skip}; rob → dp[${i - 2}] + ${nums[i]} = ${take}. dp[${i}] = ${dp[i]} — ${action}.` });
    }
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;
  const nums = input.split(',').map(s => s.trim()).filter(Boolean);
  const getHouseState = i => (cur && i === cur.i) ? { bg: `linear-gradient(135deg,${V1},${V2})`, border: V1, color: '#fff' } : {};
  const getDpState = i => {
    if (!cur) return {};
    if (i === cur.i) return { bg: `linear-gradient(135deg,${V1},${V2})`, border: V1, color: '#fff' };
    if (cur.from && cur.from.includes(i)) return { bg: `${TEAL}22`, border: TEAL, color: 'var(--text)' };
    return {};
  };

  const code = {
    lines: [
      'int rob(int[] nums) {',
      '    int n = nums.length;',
      '    if (n == 1) return nums[0];',
      '    int[] dp = new int[n];',
      '    dp[0] = nums[0];',
      '    dp[1] = Math.max(nums[0], nums[1]);',
      '    for (int i = 2; i < n; i++) {',
      '        dp[i] = Math.max(dp[i - 1], dp[i - 2] + nums[i]);',
      '    }',
      '    return dp[n - 1];',
      '}',
    ],
    active: !cur ? -1 : cur.i === 0 ? 4 : cur.i === 1 ? 5 : 7,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <BoxRow label="houses (nums)" values={nums} getBoxState={getHouseState} />
        <BoxRow label="dp[i] — max loot possible up through house i" values={cur ? cur.dp : nums.map(() => 0)} getBoxState={getDpState} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <input value={input} onChange={e => { setInput(e.target.value); setSteps(null); }} style={wideInput} placeholder="2, 7, 9, 3, 1 (max 10)" />
          <button onClick={run} style={smallBtn(V1)}>Build Table</button>
        </div>
        {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
        <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: '14px 0 0' }}>
          {!cur ? `Enter house values, then Build Table — watch each dp[i] pick the better of "skip house i" and "rob it."` : cur.note}
        </p>
      </div>
      <CodePanel title="Java — House Robber (Bottom-Up DP)" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Coin Change ─────────────────────────────────────────────────── */
const CoinChangeMode = () => {
  const [coinsInput, setCoinsInput] = useState('1, 2, 5');
  const [amountInput, setAmountInput] = useState('11');
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const run = () => {
    const coins = coinsInput.split(',').map(s => Number(s.trim())).filter(v => !isNaN(v) && v > 0);
    const amount = Math.min(20, Math.max(0, Number(amountInput) || 0));
    if (coins.length === 0) return;
    const dp = new Array(amount + 1).fill(Infinity);
    dp[0] = 0;
    const built = [];
    built.push({ dp: [...dp], amt: 0, from: [], note: 'dp[0] = 0 — zero coins needed to make amount 0.' });
    for (let amt = 1; amt <= amount; amt++) {
      let best = Infinity, usedCoin = null, from = null;
      for (const c of coins) {
        if (c <= amt && dp[amt - c] + 1 < best) {
          best = dp[amt - c] + 1;
          usedCoin = c;
          from = amt - c;
        }
      }
      dp[amt] = best;
      built.push({
        dp: [...dp], amt, from: from !== null ? [from] : [],
        note: best === Infinity
          ? `No coin fits under a reachable smaller amount for amt=${amt} — dp[${amt}] stays unreachable (∞).`
          : `Best coin at amount ${amt}: use coin ${usedCoin} → dp[${amt}] = dp[${from}] + 1 = ${dp[from]} + 1 = ${dp[amt]}.`,
      });
    }
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;
  const getDpState = i => {
    if (!cur) return {};
    if (i === cur.amt) return { bg: `linear-gradient(135deg,${V1},${V2})`, border: V1, color: '#fff' };
    if (cur.from && cur.from.includes(i)) return { bg: `${TEAL}22`, border: TEAL, color: 'var(--text)' };
    return {};
  };

  const code = {
    lines: [
      'int coinChange(int[] coins, int amount) {',
      '    int[] dp = new int[amount + 1];',
      '    Arrays.fill(dp, Integer.MAX_VALUE);',
      '    dp[0] = 0;',
      '    for (int amt = 1; amt <= amount; amt++) {',
      '        for (int coin : coins) {',
      '            if (coin <= amt && dp[amt - coin] != Integer.MAX_VALUE) {',
      '                dp[amt] = Math.min(dp[amt], dp[amt - coin] + 1);',
      '            }',
      '        }',
      '    }',
      '    return dp[amount] == Integer.MAX_VALUE ? -1 : dp[amount];',
      '}',
    ],
    active: !cur ? -1 : cur.amt === 0 ? 3 : 7,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <BoxRow label="dp[amount] — fewest coins to make each amount" values={cur ? cur.dp : []} getBoxState={getDpState} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <input value={coinsInput} onChange={e => { setCoinsInput(e.target.value); setSteps(null); }} style={wideInput} placeholder="1, 2, 5" />
          <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)' }}>amount</span>
          <input value={amountInput} onChange={e => { setAmountInput(e.target.value); setSteps(null); }} style={{ ...smallInput, width: 70 }} type="number" min={0} max={20} />
          <button onClick={run} style={smallBtn(V1)}>Build Table</button>
        </div>
        {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
        <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: '14px 0 0' }}>
          {!cur ? `Enter coins and a target amount (≤ 20), then Build Table — watch the unbounded-knapsack DP fill in.` : cur.note}
        </p>
      </div>
      <CodePanel title="Java — Coin Change (Bottom-Up, Unbounded)" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Longest Increasing Subsequence ──────────────────────────────── */
const LisMode = () => {
  const [input, setInput] = useState('10, 9, 2, 5, 3, 7, 101, 18');
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const run = () => {
    const nums = input.split(',').map(s => Number(s.trim())).filter(v => !isNaN(v));
    if (nums.length === 0 || nums.length > 8) return;
    const dp = new Array(nums.length).fill(1);
    const built = [];
    built.push({ dp: [...dp], i: 0, from: [], note: 'dp[0] = 1 — a single element is always an increasing subsequence of length 1.' });
    for (let i = 1; i < nums.length; i++) {
      let best = 1, bestJ = null;
      for (let j = 0; j < i; j++) {
        if (nums[j] < nums[i] && dp[j] + 1 > best) {
          best = dp[j] + 1;
          bestJ = j;
        }
      }
      dp[i] = best;
      built.push({
        dp: [...dp], i, from: bestJ !== null ? [bestJ] : [],
        note: bestJ !== null
          ? `nums[${i}]=${nums[i]}: best extension comes from j=${bestJ} (nums[${bestJ}]=${nums[bestJ]} < ${nums[i]}). dp[${i}] = dp[${bestJ}] + 1 = ${best}.`
          : `nums[${i}]=${nums[i]}: no earlier smaller value found — dp[${i}] stays 1 (starts its own subsequence).`,
      });
    }
    const overallBest = Math.max(...dp);
    built.push({ dp: [...dp], i: -1, from: [], done: true, note: `Every dp[i] is computed. The answer is the LARGEST value in the table: ${overallBest}.` });
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;
  const nums = input.split(',').map(s => s.trim()).filter(Boolean);
  const getNumState = i => (cur && i === cur.i) ? { bg: `linear-gradient(135deg,${V1},${V2})`, border: V1, color: '#fff' } : {};
  const getDpState = i => {
    if (!cur) return {};
    if (i === cur.i) return { bg: `linear-gradient(135deg,${V1},${V2})`, border: V1, color: '#fff' };
    if (cur.from && cur.from.includes(i)) return { bg: `${TEAL}22`, border: TEAL, color: 'var(--text)' };
    return {};
  };

  const code = {
    lines: [
      'int lengthOfLIS(int[] nums) {',
      '    int[] dp = new int[nums.length];',
      '    Arrays.fill(dp, 1);',
      '    int best = 1;',
      '    for (int i = 1; i < nums.length; i++) {',
      '        for (int j = 0; j < i; j++) {',
      '            if (nums[j] < nums[i]) {',
      '                dp[i] = Math.max(dp[i], dp[j] + 1);',
      '            }',
      '        }',
      '        best = Math.max(best, dp[i]);',
      '    }',
      '    return best;',
      '}',
    ],
    active: !cur ? -1 : cur.done ? 12 : cur.i === 0 ? 2 : 7,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <BoxRow label="nums" values={nums} getBoxState={getNumState} />
        <BoxRow label="dp[i] — longest increasing subsequence ending at i" values={cur ? cur.dp : nums.map(() => 1)} getBoxState={getDpState} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <input value={input} onChange={e => { setInput(e.target.value); setSteps(null); }} style={wideInput} placeholder="10, 9, 2, 5, 3, 7 (max 8)" />
          <button onClick={run} style={smallBtn(V1)}>Build Table</button>
        </div>
        {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
        <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: '14px 0 0' }}>
          {!cur ? `Enter a sequence (max 8 numbers), then Build Table — watch each dp[i] search all earlier smaller values.` : cur.note}
        </p>
      </div>
      <CodePanel title="Java — Longest Increasing Subsequence (O(n²) DP)" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Quick Quiz ─────────────────────────────────────────────────── */
const buildQuizScenario = () => {
  const dpPrev1 = 1 + Math.floor(Math.random() * 15);
  const dpPrev2 = 1 + Math.floor(Math.random() * 15);
  const val = 1 + Math.floor(Math.random() * 10);
  const takeVal = dpPrev2 + val;
  const correct = Math.max(dpPrev1, takeVal);
  let wrong = dpPrev1 + val;
  if (wrong === correct) wrong = takeVal > 0 ? takeVal - 1 : takeVal + 1;
  const options = Math.random() < 0.5 ? [correct, wrong] : [wrong, correct];
  return { dpPrev1, dpPrev2, val, takeVal, correct, options };
};
const QuizMode = () => {
  const [scenario, setScenario] = useState(buildQuizScenario());
  const [answer, setAnswer] = useState(null);
  const [score, setScore] = useState({ correct: 0, total: 0 });

  const pick = choice => {
    if (answer !== null) return;
    setAnswer(choice);
    setScore(s => ({ correct: s.correct + (choice === scenario.correct ? 1 : 0), total: s.total + 1 }));
  };
  const next = () => { setScenario(buildQuizScenario()); setAnswer(null); };

  return (
    <div>
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', margin: '4px 0 14px', lineHeight: 1.6 }}>
        House-Robber-style recurrence: dp[i-1] = {scenario.dpPrev1} (best loot skipping house i), and dp[i-2] + nums[i] = {scenario.dpPrev2} + {scenario.val} = {scenario.takeVal} (best loot robbing house i). What is dp[i] = max(dp[i-1], dp[i-2] + nums[i])?
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
        {scenario.options.map((opt, oi) => {
          const revealed = answer !== null;
          const isCorrect = opt === scenario.correct;
          const isPicked = opt === answer;
          let border = 'var(--border)', bg = 'transparent', color = 'var(--text)';
          if (revealed && isCorrect) { border = GREEN; bg = 'rgba(46,204,113,0.1)'; color = '#1e9e57'; }
          else if (revealed && isPicked && !isCorrect) { border = RED; bg = 'rgba(240,65,85,0.08)'; color = RED; }
          return (
            <button key={oi} onClick={() => pick(opt)} disabled={revealed} style={{
              textAlign: 'left', padding: '10px 14px', borderRadius: 10,
              border: `1.5px solid ${border}`, background: bg, color, fontSize: 13.5,
              cursor: revealed ? 'default' : 'pointer', fontFamily: "'Satoshi','DM Sans',sans-serif",
            }}>dp[i] = {opt}</button>
          );
        })}
      </div>
      {answer !== null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button onClick={next} style={smallBtn(V1)}>Next Question</button>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>Score: {score.correct} / {score.total}</span>
          <span style={{ fontSize: 12.5, color: 'var(--text)' }}>
            {scenario.takeVal > scenario.dpPrev1 ? 'Robbing house i wins here.' : 'Skipping house i wins here.'}
          </span>
        </div>
      )}
    </div>
  );
};

/* ─── Main playground ────────────────────────────────────────────── */
const Dp1DPlayground = () => {
  const [mode, setMode] = useState('climb');

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
        {MODES.map(m => (
          <button
            key={m.key}
            onClick={() => setMode(m.key)}
            style={{
              padding: '8px 14px', borderRadius: 999, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
              border: `1.5px solid ${mode === m.key ? V1 : 'var(--border)'}`,
              background: mode === m.key ? V1 : 'transparent',
              color: mode === m.key ? '#fff' : 'var(--muted)',
              fontFamily: "'Satoshi','DM Sans',sans-serif",
            }}
          >{m.label}</button>
        ))}
      </div>

      {mode === 'climb' && <ClimbStairsMode />}
      {mode === 'robber' && <RobberMode />}
      {mode === 'coinchange' && <CoinChangeMode />}
      {mode === 'lis' && <LisMode />}
      {mode === 'quiz' && <QuizMode />}

      <style>{`
        @media (max-width: 640px) {
          .dsa-playground-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default Dp1DPlayground;
